import { createSignal, onCleanup, Show, JSX } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { PreReviewPopupDetails } from "~/server/types/brand";
import { goBack, openModalV2 } from "~/shared_states/modal";

export interface WarningModalTelescopeProps {
  preReviewPopupDetails: PreReviewPopupDetails;
  onContinue: () => void;
  onClose?: () => void;
}

/**
 * Opens the Warning Modal with Telescope design system
 * Shows pre-purchase warnings with mandatory 5-second wait timer
 */
export const openWarningModalTelescope = (props: {
  preReviewPopupDetails: PreReviewPopupDetails;
  onContinue: () => void;
}) => {
  openModalV2(
    () => <WarningModalTelescope {...props} onClose={() => goBack()} />,
    {
      size: "small", // 416px width, matches original
    }
  );
};

/**
 * Warning Modal Component using Telescope Design System
 * Displays warnings before purchase with mandatory wait timer
 */
export function WarningModalTelescope(
  props: WarningModalTelescopeProps
): JSX.Element {
  const { components } = useTelescopeContext();
  const { preReviewPopupDetails } = props;

  return (
    <div class="flex w-full flex-col overflow-hidden bg-background-normal-primary">
      {/* Modal Header */}
      <components.ModalHeader
        size="large"
        title="Please note"
        description={preReviewPopupDetails.title ?? undefined}
        onClose={props.onClose}
      />

      {/* Content Section */}
      <div class="flex w-full flex-col items-center px-5 pb-5 pt-4">
        {/* Optional Header Image */}
        <Show when={preReviewPopupDetails.headerImageUrl}>
          <img
            src={preReviewPopupDetails.headerImageUrl!}
            alt=""
            class="w-full"
            height={84}
          />
          <div class="h-6" />
        </Show>

        {/* Description */}
        <Show when={preReviewPopupDetails.description}>
          <span class="text-body-regular text-center text-text-normal-secondary">
            {preReviewPopupDetails.description}
          </span>
          <div class="h-5" />
        </Show>

        {/* Warnings List */}
        <Show when={preReviewPopupDetails.warnings?.length ?? 0 > 0}>
          <WarningsList warnings={preReviewPopupDetails.warnings!} />
        </Show>
      </div>

      {/* Footer with Timed Button */}
      <TimedButton onContinue={props.onContinue} />
    </div>
  );
}

/**
 * Warnings List Component
 * Displays list of warning items with icons and messages
 */
function WarningsList(props: {
  warnings: {
    icon: string;
    message: string;
  }[];
}): JSX.Element {
  return (
    <div class="flex w-full flex-col gap-3">
      {props.warnings.map((warning) => (
        <div class="flex flex-row items-center gap-3 rounded-2xl border border-stroke-1 bg-background-normal-secondary p-4">
          <img class="h-8 w-8 flex-shrink-0" src={warning.icon} alt="" />
          <span class="text-body-medium text-text-normal-primary">
            {warning.message}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Timed Button Component
 * Shows 5-second countdown before allowing user to continue
 */
function TimedButton(props: { onContinue: () => void }): JSX.Element {
  const { components } = useTelescopeContext();
  const timeLimitInSec = 5;
  const [timeRemaining, setTimeRemaining] = createSignal(timeLimitInSec);

  // Timer countdown
  const timer = setInterval(() => {
    setTimeRemaining((prev) => {
      const newValue = prev - 1;
      if (newValue <= 0) {
        clearInterval(timer);
        return 0;
      }
      return newValue;
    });
  }, 1000);

  onCleanup(() => {
    clearInterval(timer);
  });

  return (
    <div class="border-t border-stroke-1 px-5 py-4">
      <Show
        when={timeRemaining() === 0}
        fallback={
          <components.Button
            text={`Continue in... ${timeRemaining()}`}
            onClick={() => {}}
            variant="primary"
            style="neutral"
            size={40}
            disabled={true}
            expanded={true}
          />
        }
      >
        <components.Button
          text="Get this card"
          onClick={props.onContinue}
          variant="primary"
          style="neutral"
          size={40}
          expanded={true}
        />
      </Show>
    </div>
  );
}
